import { Button, Card, Col, Flex, Row, Select, Space, Switch, Typography, message } from 'antd';
import ReactECharts from 'echarts-for-react';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import {
  useAggregateResponses,
  useCodeFrames,
  useUpdateDisplaySentiment,
} from '../../../api/Questions';
import { useSurveySummary } from '../../../api/Surveys';
import ChartExportMenu from '../../../components/ChartExportMenu';
import Loading from '../../../components/Loading';
import { SurveyDashboardTabs, SurveyUploadType, UserRoleType } from '../../../constants';
import { getChartForClosedEndedQuestions } from '../../../echarts/ChartHelper';
import useParams from '../../../hooks/useParams';
import useRoles from '../../../hooks/useRoles';
import { queryType, surveyType } from '../../../types';
import ConcernCard from './ConcernCard';
import SurveySummary from './SurveySummary';

const { Title, Text } = Typography;
const { Option } = Select;

const StyledSelect = styled(Select)`
  width: 100%;
`;

const ChartCard = styled(Card)`
  > div {
    display: flex;
    justify-content: center;
  }
`;

const StyledCard = styled(Card)`
  width: 100%;
`;

function SummaryTab({ survey, questions, setActiveTab, setSelectedOpenEndedQuestionId }) {
  const { surveyId } = useParams();
  const { roles } = useRoles();

  const openEndedQuestions = questions.data.filter((q) => q.has_themes);
  const closeEndedQuestions = questions.data.filter((q) => q.is_filterable);

  const chartRef = useRef(null);
  const [aggregatedResponsesChart, setAggregateResponsesChart] = useState(undefined);
  const [aggregatedResponsesRaw, setAggregatedResponsesRaw] = useState([]);
  const [selectedCloseEndedQuestionId, setSelectedCloseEndedQuestionId] = useState(
    closeEndedQuestions[0]?.id,
  );

  const codeFrames = useCodeFrames(
    openEndedQuestions.map((q) => ({ questionId: q.id, surveyId })),
    { select: (data) => data.data },
  );

  const { isFetching: aggregateResponsesFetching } = useAggregateResponses(
    { questionId: selectedCloseEndedQuestionId, surveyId },
    {
      enabled: !!selectedCloseEndedQuestionId,
      onSuccess: (data) => {
        const questionInfo = closeEndedQuestions.find((q) => q.id === selectedCloseEndedQuestionId);
        setAggregateResponsesChart(getChartForClosedEndedQuestions(data.data, questionInfo.text));
        setAggregatedResponsesRaw(data.data);
      },
    },
  );

  const { data: summary, isLoading: summaryLoading } = useSurveySummary(
    { surveyId },
    { keepPreviousData: true },
  );

  const { mutate: updateDisplaySentiment, isLoading: updateDisplaySentimentLoading } =
    useUpdateDisplaySentiment();

  const handleSentimentSwitch = async (checked, questionId) => {
    updateDisplaySentiment(
      {
        questionId,
        surveyId,
        data: JSON.stringify({ display_sentiment: checked }),
      },
      {
        onSuccess: () => {
          if (checked) {
            message.success('Sentiment analysis visible for all users');
          } else {
            message.success('Sentiment analysis hidden for all users');
          }
        },
      },
    );
  };

  const questionCardButtons = (questionId, displaySentiment) => {
    const buttons = [
      <Button
        key="explore-themes"
        type="link"
        onClick={() => {
          setSelectedOpenEndedQuestionId(questionId);
          setActiveTab(SurveyDashboardTabs.THEMES);
        }}
      >
        Explore themes
      </Button>,
    ];
    if (displaySentiment) {
      buttons.push(
        <Button
          key="explore-sentiments"
          type="link"
          onClick={() => {
            setSelectedOpenEndedQuestionId(questionId);
            setActiveTab(SurveyDashboardTabs.SENTIMENTS);
          }}
        >
          Explore sentiments
        </Button>,
      );
    }
    return buttons;
  };

  if (summaryLoading || codeFrames.some((cf) => cf.isLoading)) {
    return <Loading />;
  }

  return (
    <>
      {summary?.data.summary && <SurveySummary survey={survey} summary={summary.data} />}
      {openEndedQuestions.length > 0 && (
        <>
          <Title level={4}>
            {survey.upload_type === SurveyUploadType.SURVEY
              ? 'Common themes for each open-ended question'
              : 'Common themes for each interview question'}
          </Title>
          <Flex vertical gap={8}>
            {openEndedQuestions.map((question, index) => (
              <StyledCard
                key={question.id}
                title={question.text}
                extra={
                  (roles.includes(UserRoleType.ADMINISTRATOR) ||
                    roles.includes(UserRoleType.EDITOR)) && (
                    <Space>
                      <Text type="secondary">Sentiment analysis</Text>
                      <Switch
                        checked={question.display_sentiment}
                        disabled={updateDisplaySentimentLoading}
                        onChange={(checked) => handleSentimentSwitch(checked, question.id)}
                      />
                    </Space>
                  )
                }
                actions={questionCardButtons(question.id, question.display_sentiment)}
                data-cy="theme-card"
              >
                <Row>
                  {codeFrames
                    .find((cf) => cf.data?.question_id === question.id)
                    ?.data.themes_data.themes.slice(0, 3)
                    .map(({ data: theme }) => (
                      <Col key={theme.id} xs={24} md={8}>
                        <ConcernCard
                          theme={theme}
                          index={index}
                          displaySentiment={question.display_sentiment}
                        />
                      </Col>
                    ))}
                </Row>
              </StyledCard>
            ))}
          </Flex>
        </>
      )}
      {closeEndedQuestions.length > 0 && (
        <>
          <Title level={4}>Responses from multiple choice questions</Title>
          <Flex vertical gap={8}>
            <StyledSelect
              value={selectedCloseEndedQuestionId}
              loading={aggregateResponsesFetching}
              onChange={setSelectedCloseEndedQuestionId}
            >
              {closeEndedQuestions.map((q) => (
                <Option key={q.id} value={q.id}>
                  {q.text}
                </Option>
              ))}
            </StyledSelect>
            {aggregatedResponsesChart && (
              <ChartCard data-cy="echarts-chart">
                <div ref={chartRef} style={{ width: '100%', position: 'relative' }}>
                  <Row justify="end" style={{ padding: '8px 12px 0 0' }}>
                    <ChartExportMenu
                      chartRef={chartRef}
                      filename={
                        closeEndedQuestions.find((q) => q.id === selectedCloseEndedQuestionId)
                          ?.text || 'chart'
                      }
                      csvData={() =>
                        aggregatedResponsesRaw.map((r) => ({
                          Response: r.response_text,
                          'Percentage (%)': r.percentage,
                          'Respondent Count': r.respondent_count,
                        }))
                      }
                    />
                  </Row>
                  <Row justify="center">
                    <ReactECharts
                      option={aggregatedResponsesChart.option}
                      style={{ height: aggregatedResponsesChart.height, width: '65%' }}
                      opts={{ renderer: 'svg' }}
                    />
                  </Row>
                </div>
              </ChartCard>
            )}
          </Flex>
        </>
      )}
    </>
  );
}

SummaryTab.propTypes = {
  survey: surveyType.isRequired,
  questions: queryType.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  setSelectedOpenEndedQuestionId: PropTypes.func.isRequired,
};

export default SummaryTab;
